/**
 * Challenges API Functions
 * API calls for fetching and managing challenges.
 * Raw-shape mapping is handled by challengeTransform.helper.ts.
 */

import { type AxiosResponse } from 'axios';
import { API_ENDPOINTS } from '../../../constants';
import { logger } from "../../helpers/logger.helper";
import {
  ARENA_ACTIVE_LIMIT,
  ARENA_ACTIVE_STATUS,
  GROUP_CHALLENGES_STATUS_JOINABLE,
} from '../constants/challenges.constants';
import type { ChallengeWithStats } from '../../../types/challenges';
import { mapChallengeFromApi, type RawApiChallenge } from './challengeTransform.helper';

/** Typed API client function signature used throughout this module. */
type ApiClientFn = <T = unknown>(endpoint: string, config?: object) => Promise<AxiosResponse<T>>;

/** Re-exported for legacy import paths; the canonical source is `challenges.constants`. */
export { GROUP_CHALLENGES_STATUS_JOINABLE };

/**
 * Reset all challenges (development only). Requires an authenticated API client.
 */
export const resetChallenges = async (apiClientFn: ApiClientFn): Promise<boolean> => {
  try {
    const { data } = await apiClientFn<{ success: boolean }>(API_ENDPOINTS.challenges.reset, {
      method: 'DELETE',
    });
    return data?.success === true;
  } catch {
    return false;
  }
};

/** Raw API response for list endpoint (with optional hasMore for pagination). */
export interface ChallengesPageResponse {
  data: ChallengeWithStats[];
  count: number;
  hasMore: boolean;
}

/**
 * Fetch a single challenge by ID (for detail view).
 * Requires authenticated API client.
 */
export const fetchChallenge = async (
  challengeId: string,
  apiClientFn: ApiClientFn
): Promise<ChallengeWithStats | null> => {
  try {
    const { data } = await apiClientFn<{ success: boolean; data?: unknown }>(`/challenges/${challengeId}`, { method: 'GET' });
    if (!data?.success || !data.data) return null;
    return mapChallengeFromApi(data.data as RawApiChallenge) as ChallengeWithStats;
  } catch (error) {
    const err = error as { message?: string; code?: string };
    logger.error('fetchChallenge failed', { message: err?.message, code: err?.code });
    return null;
  }
};

/**
 * Fetch active challenges from backend API (one shot, no pagination).
 * Requires authenticated API client (Supabase JWT token).
 */
export const fetchActiveChallenges = async (
  _userId: string,
  apiClientFn: ApiClientFn,
  status?: string,
  limit?: number
): Promise<ChallengeWithStats[]> => {
  try {
    const params = new URLSearchParams();
    params.set('status', status ?? ARENA_ACTIVE_STATUS);
    params.set('limit', String(limit ?? ARENA_ACTIVE_LIMIT));
    const { data } = await apiClientFn<{ success: boolean; data?: RawApiChallenge[] }>(
      `/challenges?${params.toString()}`, { method: 'GET' }
    );
    if (!data?.success || !Array.isArray(data.data)) return [];
    return data.data.map((c) => mapChallengeFromApi(c));
  } catch (error) {
    const err = error as { message?: string; code?: string };
    logger.error('fetchActiveChallenges failed', { message: err?.message, code: err?.code });
    throw error;
  }
};

/**
 * Fetch ALL PENDING+ACTIVE challenges for the Arena by walking pages until the
 * server reports `hasMore: false`. Capped at `maxPages` as a runaway-guard —
 * anything past that is almost certainly a bug, not real UX (logged at warn).
 *
 * Returns a flat ChallengeWithStats[] so React Query consumers don't need to
 * change shape; concurrent refetches behave like a normal useQuery — previous
 * data stays visible until the new walk finishes.
 */
export const fetchAllActiveChallenges = async (
  apiClientFn: ApiClientFn,
  status: string,
  pageSize: number,
  maxPages: number,
): Promise<ChallengeWithStats[]> => {
  const all: ChallengeWithStats[] = [];
  let offset = 0;
  for (let page = 0; page < maxPages; page += 1) {
    // Sequential by design; each page depends on hasMore from the previous to decide
    // whether another request fires.
    const { data, hasMore } = await fetchChallengesPage(apiClientFn, status, pageSize, offset);
    all.push(...data);
    if (!hasMore) return all;
    offset += pageSize;
  }
  logger.warn('fetchAllActiveChallenges hit maxPages safety cap', {
    maxPages,
    pageSize,
    status,
    loaded: all.length,
  });
  return all;
};

/**
 * Fetch a page of challenges (for paginated history).
 * Returns data, count, and hasMore for infinite query.
 */
export const fetchChallengesPage = async (
  apiClientFn: ApiClientFn,
  status: string,
  limit: number,
  offset: number
): Promise<ChallengesPageResponse> => {
  try {
    const endpoint = `/challenges?status=${encodeURIComponent(status)}&limit=${limit}&offset=${offset}`;
    const { data } = await apiClientFn<{ success: boolean; data?: RawApiChallenge[]; count?: number; hasMore?: boolean }>(
      endpoint, { method: 'GET' }
    );
    if (!data?.success || !Array.isArray(data.data)) return { data: [], count: 0, hasMore: false };
    const list = data.data.map((c) => mapChallengeFromApi(c));
    return { data: list, count: data.count ?? list.length, hasMore: data.hasMore ?? false };
  } catch (error) {
    const err = error as { message?: string; code?: string };
    logger.error('fetchChallengesPage failed', { message: err?.message, code: err?.code });
    throw error;
  }
};

/**
 * Fetch challenges for a group. Optional status: COMPLETED (default) or PENDING,ACTIVE (joinable).
 */
export const fetchGroupChallenges = async (
  groupId: string,
  apiClientFn: ApiClientFn,
  status?: string
): Promise<ChallengeWithStats[]> => {
  try {
    const endpoint = status
      ? `/groups/${groupId}/challenges?status=${encodeURIComponent(status)}`
      : `/groups/${groupId}/challenges`;
    const { data } = await apiClientFn<{ success: boolean; data?: RawApiChallenge[] }>(endpoint, { method: 'GET' });
    if (!data?.success || !Array.isArray(data.data)) return [];
    return data.data.map((c) => mapChallengeFromApi(c));
  } catch (error) {
    const err = error as { message?: string; code?: string };
    logger.error('fetchGroupChallenges failed', { message: err?.message, code: err?.code });
    throw error;
  }
};

/** Response shape for paginated group challenges (GET /groups/:id/challenges?limit=&offset=). */
export interface GroupChallengesPageResponse {
  data: ChallengeWithStats[];
  hasMore: boolean;
}

/**
 * Fetch a page of group challenges (for infinite scroll on group leaderboard).
 */
export const fetchGroupChallengesPage = async (
  groupId: string,
  apiClientFn: ApiClientFn,
  limit: number,
  offset: number,
  status: string = 'COMPLETED'
): Promise<GroupChallengesPageResponse> => {
  try {
    const endpoint = `/groups/${groupId}/challenges?status=${encodeURIComponent(status)}&limit=${limit}&offset=${offset}`;
    const { data } = await apiClientFn<{ success: boolean; data?: RawApiChallenge[]; hasMore?: boolean }>(
      endpoint, { method: 'GET' }
    );
    if (!data?.success || !Array.isArray(data.data)) return { data: [], hasMore: false };
    const list = data.data.map((c) => mapChallengeFromApi(c));
    return { data: list, hasMore: data.hasMore === true };
  } catch (error) {
    const err = error as { message?: string; code?: string };
    logger.error('fetchGroupChallengesPage failed', { message: err?.message, code: err?.code });
    throw error;
  }
};
